import { Controller } from "@hotwired/stimulus"
import { allBundlesStore } from "../../stores/device_types/all_bundles_store";
import { allDevicesStore } from "../../stores/devices/all_devices_store";
import { manufacturerStore } from "../../stores/device_types/manufacturer_store";
import { modelStore } from "../../stores/device_types/model_store";
import { deviceTypeStore } from "../../stores/device_types/device_type_store";
import { roomStore } from "../../stores/locales/room_store";
import { areaStore } from "../../stores/locales/area_store";
import { buildingStore } from "../../stores/locales/building_store";
import { regionStore } from "../../stores/locales/region_store";
import { deploymentStatusStore } from "../../stores/deployment_status_store";
import { bundleStore } from "../../stores/device_types/bundle_store";
import { useStore } from "stimulus-store";
import { getCsrfToken, isValidUuid } from "shared/form_helpers";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { getModels } from "shared/device_types/models";
import { getArea } from "shared/locales/locales";
import { getDevice } from "shared/devices/devices";

// Connects to data-controller="forms--item-form"
//
// TURBO DRIVE NOTE: All page-specific stores declared below (regionStore,
// buildingStore, areaStore, roomStore, deviceTypeStore, manufacturerStore,
// modelStore, bundleStore, deploymentStatusStore) are module-level singletons
// that persist across Turbo Drive navigations. The locale stores
// (region/building/area/room) are cleared by their own select controllers'
// disconnect() methods when leaving the previous page (see
// region_select_controller.js), so both "create" and "update" mode should see
// a clean slate before this controller connects. The device-type stores
// (deviceTypeStore, manufacturerStore, modelStore) may also carry values from
// a previous Walkthrough palette
// selection.
export default class extends Controller {

    static stores = [
        allBundlesStore,
        allDevicesStore,
        deviceTypeStore,
        manufacturerStore,
        modelStore,
        bundleStore,
        deploymentStatusStore,
        regionStore,
        buildingStore,
        areaStore,
        roomStore
    ]

    static targets = [
        "form",
        "submitButton",
        "loadingIndicator"
    ]

    static values = {
        mode: String,
        redirectUrl: String,
        coordinatesEnabled: Boolean
    }

    connect() {
        useStore(this);
        this.checkForAreaPreselection();
        
        // Populate current device for update mode
        this.populateCurrentDevice();
    }

    /**
     * Check URL parameters for area ID and trigger area preselection if found
     * This enables deep linking to specific areas in the walkthrough
     */
    checkForAreaPreselection() {
        // In the fullness of time, I want to use this controller to populate the update device, update bundle, and walkthrough forms
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get('id');
        const areaId = urlParams.get('area');
        const deviceId = urlParams.get('device');
        const bundleId = urlParams.get('bundle');

        if (areaId && isValidUuid(areaId)) {
            this.preselectArea(areaId);
        }
    }

    /**
     * Preselect an area by fetching its hierarchy and triggering the cascade selection
     * @param {string} areaId - The area ID to preselect
     */
    preselectArea(areaId) {
        // Set the area store value
        getArea(areaId).then((area) => {
            this.setRegionStoreValue({ id: area.region });
            this.setBuildingStoreValue({ id: area.building });
            this.setAreaStoreValue(area);
        });
    }

    /**
     * Populate the allDevicesStore with the current device being updated
     * This enables the floor plan icon manager to render the device icon
     */
    async populateCurrentDevice() {
        // Only populate for update mode, not create mode
        if (this.modeValue !== 'update') {
            return;
        }

        try {
            // Get the device ID from URL parameters (already parsed in checkForAreaPreselection)
            const urlParams = new URLSearchParams(window.location.search);
            const deviceId = urlParams.get('device');
            if (!deviceId || !isValidUuid(deviceId)) {
                console.warn('Unable to find requested device');
                return;
            }

            // Fetch the current device using the helper method
            const device = await getDevice(deviceId);

            if (device) {
                // Set the allDevicesStore with just this device
                // This will trigger the floor plan icon manager to render it
                this.setAllDevicesStoreValue([device]);
            }

        } catch (error) {
            console.error('Error populating current device:', error);
            // Don't show user error - this is a nice-to-have feature
            // The form will still work without the floor plan icon
        }
    }

    // TODO: SIMPLIFY THIS FUNCTION'S LOGIC. IT'S HARD TO FOLLOW
    async submitForm(event) {
        // Prevent default form submission behavior
        event.preventDefault();
        event.stopPropagation();

        try {
            this.showLoadingState()

            const formData = await this.extractFormData()
            const endpoint = this.determineApiEndpoint()

            /*
             * PUT /items/:id input example:
                {
                  id: device.id,
                  name: new_name,
                  device: {
                    device_type: new_device_type.id,
                    manufacturer: new_manufacturer.id,
                    model: new_model.id
                  },
                  deployment_status: new_deployment_status.id,
                  locale: {
                    region: new_region.id,
                    building: new_building.id,
                    area: new_area.id,
                    room: new_room.id,
                    floorplan_x_coordinate: new_x_coordinate,
                    floorplan_y_coordinate: new_y_coordinate
                  },
                  ip_address: new_ip_address,
                  mac_address: new_mac_address,
                  dns_name: new_dns_name,
                  port: new_port,
                  testing_tasks: [ { id: testing_task.id, status: status.id } ]
                }
             *
             * POST /items input example:
                {
                  locale: {
                    region: new_region.id,
                    building: new_building.id,
                    area: new_area.id,
                    room: new_room.id,
                    floorplan_x_coordinate: new_x_coordinate,
                    floorplan_y_coordinate: new_y_coordinate
                  },
                  devices: [
                    {
                      name: new_name,
                      type: {
                        device_type: new_device_type.id,
                        manufacturer: new_manufacturer.id,
                        model: new_model.id
                      },
                      deployment_status: new_deployment_status.id,
                      ip_address: new_ip_address,
                      mac_address: new_mac_address,
                      dns_name: new_dns_name,
                      port: new_port,
                      testing_tasks: [ { id: testing_task.id, status: status.id } ]
                    }
                  ],
                }
             */

            const response = await this.makeApiCall(endpoint, formData)

            if (response.ok) {
                const responseData = await response.json();
                const isUpdate = this.modeValue === 'update';

                if (isUpdate) {
                    // For updates, show simple success message
                    sendSuccessNotification('Device updated successfully');
                } else {
                    // For creates, add newly created devices to the allDevicesStore
                    // API response structure: { status: "success", data: { items: [...], count: N } }
                    const items = responseData.data?.items || responseData.items;
                    if (items && Array.isArray(items)) {
                        const currentDevices = this.allDevicesStoreValue || [];
                        // Transform created devices to match the schema expected by the list method
                        // The create API returns a flat structure, but the list API returns nested objects
                        const transformedDevices = items.map(item => this.transformCreatedDeviceToListSchema(item));
                        const updatedDevices = [...currentDevices, ...transformedDevices];
                        this.setAllDevicesStoreValue(updatedDevices);
                    }

                    // Clear coordinate fields if coordinates are enabled (walkthrough page)
                    // Note: We don't clear the dropped icon because the store update will automatically
                    // render the proper device icon with correct colors and tooltip
                    if (this.coordinatesEnabledValue) {
                        this.clearCoordinateFields();
                    }

                    // Show the appropriate success message based on what was created
                    const itemCount = items ? items.length : 1;
                    const bundleId = this.bundleStoreValue?.id;
                    const itemType = bundleId ? 'bundle' : (itemCount > 1) ? 'items' : 'device';
                    sendSuccessNotification(`Successfully created ${ itemCount } ${ itemType }${ itemCount > 1 ? 's' : '' }`);
                }

                // Optionally redirect if redirectUrl is provided
                if (this.redirectUrlValue) {
                    // We're giving the user a second to read the success message before redirecting
                    // them to a fresh page.
                    setTimeout(() => {
                        window.location.href = this.redirectUrlValue;
                    }, 1000);
                }
            } else {
                const errorData = await response.json()
                const isUpdate = this.modeValue === 'update';
                const operation = isUpdate ? 'update' : 'create';
                console.error('API Error Response:', errorData);
                sendFailureNotification(errorData.message || `Failed to ${ operation } item`);
            }
        } catch (error) {
            // Handle different types of errors appropriately
            console.error('Form submission error:', error);
            const isUpdate = this.modeValue === 'update';
            const operation = isUpdate ? 'update' : 'create';

            let errorMessage = `Unable to ${ operation } item`;
            if (error.message) {
                errorMessage += `: ${ error.message }`;
            }

            sendFailureNotification(errorMessage);
        } finally {
            this.hideLoadingState();
        }
    }

    async extractFormData() {
        const isUpdate = this.modeValue === 'update';

        if (isUpdate) {
            return this.buildUpdatePayload();
        }

        const bundleId = this.bundleStoreValue?.id;
        if (bundleId) {
            return await this.buildBundleCreationPayload(bundleId);
        }

        return this.buildSingleDeviceCreationPayload();
    }

    /**
     * Build payload for updating an existing device
     * PUT /items/:id
     */
    buildUpdatePayload() {
        const updateData = {};

        // Add device ID (required for updates)
        const deviceId = this.getFieldValue('id');
        if (deviceId) {
            updateData.id = deviceId;
        }

        // Add device name if available
        const name = this.getFieldValue('name');
        if (name) {
            updateData.name = name;
        }

        // Add device type information from stores (nested under 'device')
        const deviceTypeData = this.extractDeviceTypeFromStores();
        if (deviceTypeData) {
            updateData.device = deviceTypeData;
        }

        // Add deployment status if available
        const deploymentStatus = this.deploymentStatusStoreValue?.id;
        if (deploymentStatus) {
            updateData.deployment_status = deploymentStatus;
        }

        // Add location hierarchy from stores (nested under 'locale')
        const locale = this.extractLocaleData();
        if (Object.keys(locale).length > 0) {
            updateData.locale = locale;
        }

        // Add network information from form fields
        const networkData = this.extractNetworkData();
        Object.assign(updateData, networkData);

        // Add epic_id if available
        const epicId = this.getFieldValue('epic_id');
        if (epicId !== null) {
            updateData.epic_id = epicId;
        }

        // Add testing task statuses if any are selected
        const testingTasks = this.extractTestingTaskStatuses();
        if (testingTasks.length > 0) {
            updateData.testing_tasks = testingTasks;
        }

        // Add print mappings if any are configured (workstations)
        const printMappings = this.extractPrintMappings();
        if (printMappings.length > 0) {
            updateData.print_mapping = printMappings;
        }

        // Add tray mappings if any are configured (printers)
        const trayMappings = this.extractTrayMappings();
        if (trayMappings.length > 0) {
            updateData.tray_mappings = trayMappings;
        }

        return updateData;
    }

    /**
     * Build payload for creating a single device
     * POST /items
     */
    buildSingleDeviceCreationPayload() {
        const requestData = {};

        // Add locale information
        const locale = this.extractLocaleData();
        if (Object.keys(locale).length > 0) {
            requestData.locale = locale;
        }

        // Build single device
        const device = this.buildDeviceObject();
        requestData.devices = [device];

        return requestData;
    }

    /**
     * Build payload for creating a bundle (multiple devices)
     * POST /items
     */
    async buildBundleCreationPayload(bundleId) {
        // Validate required fields for bundle creation
        this.validateBundleCreationRequirements();

        const requestData = {
            bundle: bundleId
        };

        // Add locale information
        const locale = this.extractLocaleData();
        if (Object.keys(locale).length > 0) {
            requestData.locale = locale;
        }

        // Build devices from bundle models
        const devices = await this.buildDevicesFromBundle(bundleId);
        requestData.devices = devices;

        return requestData;
    }

    /**
     * Extract device type hierarchy from Stimulus stores
     * Returns {device_type, manufacturer, model} or null
     */
    extractDeviceTypeFromStores() {
        const deviceTypeId = this.deviceTypeStoreValue?.id;
        const manufacturerId = this.manufacturerStoreValue?.id;
        const modelId = this.modelStoreValue?.id;

        if (deviceTypeId && manufacturerId && modelId) {
            return {
                device_type: deviceTypeId,
                manufacturer: manufacturerId,
                model: modelId
            };
        }

        return null;
    }

    /**
     * Extract location hierarchy and coordinates from Stimulus stores
     * Returns locale object with region, building, area, room, and optional coordinates
     */
    extractLocaleData() {
        const locale = {};

        if (this.regionStoreValue?.id) {
            locale.region = this.regionStoreValue.id;
        }
        if (this.buildingStoreValue?.id) {
            locale.building = this.buildingStoreValue.id;
        }
        if (this.areaStoreValue?.id) {
            locale.area = this.areaStoreValue.id;
        }
        if (this.roomStoreValue?.id) {
            locale.room = this.roomStoreValue.id;
        }

        // Add coordinates if enabled (for Walkthrough page)
        if (this.coordinatesEnabledValue) {
            const xCoord = this.getFieldValue('floorplan_x_coordinate');
            const yCoord = this.getFieldValue('floorplan_y_coordinate');
            if (xCoord && xCoord !== '0') {
                locale.floorplan_x_coordinate = xCoord;
            }
            if (yCoord && yCoord !== '0') {
                locale.floorplan_y_coordinate = yCoord;
            }
        }

        return locale;
    }

    /**
     * Extract network information from form fields
     * Returns object with ip_address, mac_address, dns_name, port/port_id
     */
    extractNetworkData() {
        const networkData = {};

        const ipAddress = this.getFieldValue('ip_address');
        const macAddress = this.getFieldValue('mac_address');
        const dnsName = this.getFieldValue('dns_name');
        const portId = this.getFieldValue('port_id');

        if (ipAddress) {
            networkData.ip_address = ipAddress;
        }
        if (macAddress) {
            networkData.mac_address = macAddress;
        }
        if (dnsName) {
            networkData.dns_name = dnsName;
        }
        if (portId) {
            // For updates: API expects 'port'
            // For creates: API expects 'port_id' in devices array
            // Send port_id for consistency with model attribute name
            networkData.port_id = portId;
        }

        return networkData;
    }

    /**
     * Extract testing task statuses from testing task cards
     * Returns array of objects with task ID and selected status ID
     * Format: [{ id: taskId, state: statusId }, ...]
     */
    extractTestingTaskStatuses() {
        // Find the testing task cards container
        const container = document.querySelector('#testing-task-card-container');
        if (!container) {
            return [];
        }

        // Find all checked radio buttons with task/status data
        const checkedRadios = container.querySelectorAll('input[type="radio"]:checked[data-task-id][data-status-id]');

        // Map to API format
        const testingTasks = Array.from(checkedRadios).map(radio => ({
            id: radio.dataset.taskId,
            state: radio.dataset.statusId
        }));

        return testingTasks.length > 0 ? testingTasks : [];
    }

    /**
     * Extract print mappings from print mapping widget
     * Returns array of objects with print_class and device (printer) IDs
     * Format: [{ print_class: printClassId, device: printerId }, ...]
     */
    extractPrintMappings() {
        // Find the print mapping container
        const container = document.querySelector('#print-mapping-information-container');
        if (!container) {
            return [];
        }

        // Find all print mapping rows (excluding the template)
        const rows = container.querySelectorAll('.print-mapping.nested-form-wrapper');

        // Map to API format
        const printMappings = Array.from(rows).map(row => {
            // Find the print class select and printer select in this row
            const printClassSelect = row.querySelector('.print-class-select');
            const printerSelect = row.querySelector('.printer-select');

            const printClassValue = printClassSelect?.tomselect?.getValue();
            const printerValue = printerSelect?.tomselect?.getValue();

            // Only include if both are selected
            if (printClassSelect && printerSelect && printClassValue && printerValue) {
                return {
                    print_class: printClassValue,
                    device: printerValue
                };
            }
            return null;
        }).filter(mapping => mapping !== null);

        return printMappings.length > 0 ? printMappings : [];
    }

    /**
     * Extract tray mappings from tray mapping widget
     * Returns array of objects with tray name as key and paper type ID as value
     * Format: [{ "tray1": paper_type_id }, { "tray2": paper_type_id }, ...]
     */
    extractTrayMappings() {
        // Find the tray mapping container
        const container = document.querySelector('#tray-mapping-information-container');
        if (!container) {
            return [];
        }

        // Find all tray mapping rows (excluding the template)
        const rows = container.querySelectorAll('.tray-mapping.nested-form-wrapper');

        // Map to API format
        const trayMappings = Array.from(rows).map(row => {
            // Find the tray name input and paper type select in this row
            const trayNameInput = row.querySelector('.tray-name-input');
            const paperTypeSelect = row.querySelector('.paper-type-select');

            // Only include if both tray name and paper type are provided
            if (trayNameInput && paperTypeSelect && trayNameInput.value && paperTypeSelect.value) {
                // Return object with tray name as key and paper type ID as value
                return {
                    [trayNameInput.value]: paperTypeSelect.value
                };
            }
            return null;
        }).filter(mapping => mapping !== null);

        return trayMappings.length > 0 ? trayMappings : [];
    }

    /**
     * Build a single device object for creation
     * Used for single device creation (not bundles)
     */
    buildDeviceObject() {
        const device = {};

        // Add device type information from stores
        const deviceTypeData = this.extractDeviceTypeFromStores();
        if (deviceTypeData) {
            device.type = deviceTypeData;
        }

        // Add deployment status if available
        const deploymentStatus = this.deploymentStatusStoreValue?.id;
        if (deploymentStatus) {
            device.deployment_status = deploymentStatus;
        }

        // Add form field values
        const name = this.getFieldValue('name');
        if (name) {
            device.name = name;
        }

        // Add network information
        const networkData = this.extractNetworkData();
        Object.assign(device, networkData);

        // Add testing task statuses if any are selected
        const testingTasks = this.extractTestingTaskStatuses();
        if (testingTasks.length > 0) {
            device.testing_tasks = testingTasks;
        }

        // Add print mappings if any are configured (workstations)
        const printMappings = this.extractPrintMappings();
        if (printMappings.length > 0) {
            device.print_mapping = printMappings;
        }

        // Add tray mappings if any are configured (printers)
        const trayMappings = this.extractTrayMappings();
        if (trayMappings.length > 0) {
            device.tray_mappings = trayMappings;
        }

        return device;
    }

    /**
     * Build devices array from bundle models
     * Fetches model details and creates device objects for each model in the bundle
     */
    async buildDevicesFromBundle(bundleId) {
        // First try to get bundle data from bundleStore (walkthrough mode)
        let selectedBundle = this.bundleStoreValue;

        // If bundleStore doesn't have models, try allBundlesStore (other pages)
        if (!selectedBundle?.models?.length) {
            selectedBundle = this.allBundlesStoreValue?.find(bundle => bundle.id === bundleId);
        }

        if (!selectedBundle) {
            throw new Error(`Bundle with ID ${ bundleId } not found in stores`);
        }

        if (!selectedBundle.models?.length) {
            throw new Error(`Bundle ${ selectedBundle.name || bundleId } has no models defined`);
        }

        try {
            const modelDetails = await getModels(selectedBundle.models);
            const devices = [];

            modelDetails.forEach(model => {
                const device = {
                    type: {
                        device_type: model.device_type,
                        manufacturer: model.manufacturer,
                        model: model.model
                    }
                };

                // Add deployment status (required for bundle creation)
                const deploymentStatus = this.deploymentStatusStoreValue?.id;
                if (deploymentStatus) {
                    device.deployment_status = deploymentStatus;
                } else {
                    throw new Error('Deployment status is required for bundle creation');
                }

                devices.push(device);
            });

            return devices;
        } catch (error) {
            console.error('Error fetching model details for bundle:', error);
            throw new Error(`Failed to load bundle model details: ${ error.message }`);
        }
    }


    /**
     * Get field value from FormData
     * More reliable than querySelector and handles timing issues better
     */
    getFieldValue(fieldName) {
        const formData = new FormData(this.formTarget);
        return formData.get(fieldName) || null;
    }

    determineApiEndpoint() {
        const isUpdate = this.modeValue === 'update'

        if (isUpdate) {
            // For updates, we need the item ID from the form
            const itemId = this.getFieldValue('id')
            if (!itemId) {
                throw new Error('Device ID is required for update operations')
            }
            return `/api/items/${ itemId }`
        }

        return '/api/items'
    }

    async makeApiCall(endpoint, data) {
        const method = this.modeValue === 'update' ? 'PUT' : 'POST'

        return fetch(endpoint, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken()
            },
            body: JSON.stringify(data)
        })
    }


    showLoadingState() {
        // Disable submit button to prevent duplicate submissions
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true

            // Store original button text if not already stored
            if (!this.submitButtonTarget.dataset.originalText) {
                this.submitButtonTarget.dataset.originalText = this.submitButtonTarget.textContent
            }

            // Update button text to show loading state
            this.submitButtonTarget.textContent = 'Processing...'
        }

        // Show loading indicator if present
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.style.display = 'block'
        }

        // Disable the entire form to prevent any interactions
        if (this.hasFormTarget) {
            this.formTarget.style.pointerEvents = 'none'
            this.formTarget.style.opacity = '0.7'
        }
    }

    hideLoadingState() {
        // Re-enable submit button
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false

            // Restore original button text
            const originalText = this.submitButtonTarget.dataset.originalText
            if (originalText) {
                this.submitButtonTarget.textContent = originalText
            }
        }

        // Hide loading indicator
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.style.display = 'none'
        }

        // Re-enable form interactions
        if (this.hasFormTarget) {
            this.formTarget.style.pointerEvents = 'auto'
            this.formTarget.style.opacity = '1'
        }
    }

    /**
     * Clear only coordinate fields after successful device creation
     * The dropped icon is not cleared because the store update will automatically
     * render the proper device icon with correct colors and tooltip
     */
    clearCoordinateFields() {
        // Clear coordinate fields using more reliable element selection
        // TODO: REFACTOR TO USE STIMULUS TARGETS
        const xFieldElement = this.formTarget.querySelector('[name="floorplan_x_coordinate"]');
        if (xFieldElement) {
            xFieldElement.value = '';
        }

        // TODO: REFACTOR TO USE STIMULUS TARGETS
        const yFieldElement = this.formTarget.querySelector('[name="floorplan_y_coordinate"]');
        if (yFieldElement) {
            yFieldElement.value = '';
        }
    }

    /**
     * Clear dropped icon and coordinate fields after successful device creation
     * This is used on the walkthrough page to reset the UI for the next device
     * @deprecated Use clearCoordinateFields() instead to avoid clearing the icon that gets replaced by store update
     */
    clearDroppedIconAndCoordinates() {
        // Clear coordinate fields
        this.clearCoordinateFields();

        // Clear dropped icon by finding and calling the device drop controller
        const deviceDropController = this.findDeviceDropController();
        if (deviceDropController && deviceDropController.clearDroppedIcon) {
            deviceDropController.clearDroppedIcon();
        }
    }

    /**
     * Find the floor plan icon manager controller on the page
     */
    findDeviceDropController() {
        // Look for elements with the floor plan icon manager controller
        const elements = document.querySelectorAll('[data-controller*="floorplans--floor-plan-icon-manager"]');

        for (const element of elements) {
            const controller = this.application.getControllerForElementAndIdentifier(
                element,
                'floorplans--floor-plan-icon-manager'
            );
            if (controller) {
                return controller;
            }
        }

        return null;
    }

    /**
     * Transform a device from the create response format to match the list response format
     * This ensures consistency between devices fetched via list and devices added after creation
     */
    transformCreatedDeviceToListSchema(createdDevice) {
        // Get names from stores when available
        const regionName = this.regionStoreValue?.name || null;
        const buildingName = this.buildingStoreValue?.name || null;
        const areaName = this.areaStoreValue?.name || null;
        const roomName = this.roomStoreValue?.name || null;
        const deploymentStatusName = this.deploymentStatusStoreValue?.name || null;

        // Transform the flat create response to the nested list response structure
        const transformedDevice = {
            id: createdDevice.id,
            name: createdDevice.name,
            ip_address: createdDevice.ip_address,
            mac_address: createdDevice.mac_address,
            dns_name: createdDevice.dns_name,
            port_id: createdDevice.port_id,
            created_at: createdDevice.created_at,
            updated_at: createdDevice.updated_at,

            // Transform locale information to nested structure
            locale: {
                region: {
                    id: createdDevice.region,
                    name: regionName
                },
                building: {
                    id: createdDevice.building,
                    name: buildingName
                },
                area: {
                    id: createdDevice.area,
                    name: areaName
                },
                room: {
                    id: createdDevice.room,
                    name: roomName
                },
                floorplan_x_coordinate: createdDevice.floorplan_x_coordinate || "0",
                floorplan_y_coordinate: createdDevice.floorplan_y_coordinate || "0"
            },

            // Transform deployment status (if available)
            deployment_status: {
                id: createdDevice.deployment_status_id || createdDevice.deployment_status,
                name: deploymentStatusName
            }
        };

        // Add device-specific attributes if this is a device (has device_type)
        if (createdDevice.device_type) {
            // Get names from stores when available
            const deviceTypeName = this.deviceTypeStoreValue?.name || null;
            const manufacturerName = this.manufacturerStoreValue?.name || null;
            const modelName = this.modelStoreValue?.name || null;

            transformedDevice.device = {
                device_type: {
                    id: createdDevice.device_type,
                    name: deviceTypeName
                },
                manufacturer: {
                    id: createdDevice.manufacturer,
                    name: manufacturerName
                },
                model: {
                    id: createdDevice.model,
                    name: modelName
                }
            };
        }

        // Add part-specific attributes if this is a part (has part_type)
        if (createdDevice.part_type) {
            transformedDevice.part = {
                part_type: {
                    id: createdDevice.part_type,
                    name: null // We don't have the name from create response
                }
            };
        }

        // Add bundle-specific attributes if this is a bundle item
        if (createdDevice.bundle) {
            // Get bundle name from store when available
            const bundleName = this.bundleStoreValue?.name || null;

            transformedDevice.bundle = {
                id: createdDevice.bundle,
                name: bundleName
            };
            transformedDevice.bundle_instance_id = createdDevice.bundle_instance_id;
        }

        return transformedDevice;
    }

    /**
     * Validate that all required fields are present for bundle creation
     */
    validateBundleCreationRequirements() {
        const errors = [];

        // Check deployment status is selected
        if (!this.deploymentStatusStoreValue?.id) {
            errors.push('Deployment status must be selected for bundle creation');
        }

        // Check room is selected
        if (!this.roomStoreValue?.id) {
            errors.push('Room must be selected for bundle creation');
        }

        // Check area is selected (required for room validation)
        if (!this.areaStoreValue?.id) {
            errors.push('Area must be selected for bundle creation');
        }

        // Check building is selected (required for area validation)
        if (!this.buildingStoreValue?.id) {
            errors.push('Building must be selected for bundle creation');
        }

        // Check region is selected (required for building validation)
        if (!this.regionStoreValue?.id) {
            errors.push('Region must be selected for bundle creation');
        }

        // Check bundle has models
        const bundleData = this.bundleStoreValue;
        if (!bundleData?.models?.length) {
            errors.push('Selected bundle has no devices defined');
        }

        if (errors.length > 0) {
            throw new Error(errors.join('. '));
        }
    }
}